import React, { useState } from 'react';
import css from './ContactUsForm.module.css';
import IconCard from '../SavedCardDetails/IconCard/IconCard';
import { FormattedMessage, useIntl } from '../../util/reactIntl';

const ContactUsForm = () => {
  const intl = useIntl();
  const [formData, setFormData] = useState({
    FNAME: '',
    EMAIL: '',
    PHONE: '',
    MESSAGE: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [messageCharCount, setMessageCharCount] = useState(0);
  const MAX_MESSAGE_LENGTH = 100;

  const validateEmail = (value) => {
    if (!value || value.trim().length === 0) return 'Email is required';

    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
    if (!emailRe.test(value)) return 'Please enter a valid email';

    const fakeDomains = [
      'yopmail.com', '10minutemail.com', 'guerrillamail.com', 'mailinator.com',
      'temp-mail.org', 'throwaway.email', 'tempmail.net', 'getnada.com',
      '0-mail.com', '10minutemail.net', '20minutemail.com', 'guerrillamailblock.com',
      'spam4.me', 'tempail.com', 'tempemail.com', 'temporaryemail.net',
      'throwawaymail.com', 'trashmail.com', 'guerrillamail.net', 'guerrillamail.org'
    ];

    const domain = value.toLowerCase().split('@')[1];
    if (fakeDomains.includes(domain)) {
      return 'Please use a valid email address';
    }
    return null;
  };

  const validateName = (value) => {
    if (!value || value.trim().length === 0) return 'Name is required';
    if (value.trim().length < 2) return 'Name must be at least 2 characters';
    return null;
  };

  const validateMessage = (value) => {
    if (!value || value.trim().length === 0) return 'Message is required';
    if (value.trim().length < 10) return 'Message must be at least 10 characters';
    if (value.length > MAX_MESSAGE_LENGTH) return `Message cannot exceed ${MAX_MESSAGE_LENGTH} characters`;
    return null;
  };

  const handleBlur = (field) => {
    let error = null;
    switch(field) {
      case 'FNAME':
        error = validateName(formData.FNAME);
        break;
      case 'EMAIL':
        error = validateEmail(formData.EMAIL);
        break;
      case 'MESSAGE':
        error = validateMessage(formData.MESSAGE);
        break;
      default:
        break;
    }
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Update character count for message field
    if (name === 'MESSAGE') {
      setMessageCharCount(value.length);
      // Don't allow typing beyond max length
      if (value.length > MAX_MESSAGE_LENGTH) {
        return;
      }
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error on change if field had error
    if (errors[name]) {
      let error = null;
      switch(name) {
        case 'FNAME':
          error = validateName(value);
          break;
        case 'EMAIL':
          error = validateEmail(value);
          break;
        case 'MESSAGE':
          error = validateMessage(value);
          break;
        default:
          break;
      }
      setErrors(prev => ({ ...prev, [name]: error }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all fields
    const nameError = validateName(formData.FNAME);
    const emailError = validateEmail(formData.EMAIL);
    const messageError = validateMessage(formData.MESSAGE);
    
    if (nameError || emailError || messageError) {
      setErrors({
        FNAME: nameError,
        EMAIL: emailError,
        MESSAGE: messageError
      });
      return;
    }

    setLoading(true);
    setErrors({});
    setSuccessMessage('');

    const form = e.target;
    const formDataToSend = new FormData(form);

    try {
      await fetch(form.action, {
        method: 'POST',
        mode: 'no-cors',
        body: formDataToSend,
      });
      setSuccessMessage('Thank you! We\'ll get back to you soon.');
      setFormData({ FNAME: '', EMAIL: '', PHONE: '', MESSAGE: '' });
      setMessageCharCount(0);
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
    } catch (error) {
      console.error('Contact form error:', error);
      setErrors({ submit: 'There was an issue submitting your message. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const hasErrors = errors.FNAME || errors.EMAIL || errors.MESSAGE;
  const isFormEmpty = !formData.FNAME || !formData.EMAIL || !formData.MESSAGE;

  return (
    <div className={css.formContainer}>
      <form
        action="https://beautyrents.us6.list-manage.com/subscribe/post?u=0530e140d1a3522fe688bd3e1&id=e01c29c204"
        method="post"
        className={css.contactForm}
        onSubmit={handleSubmit}
        noValidate
      >
        <div className={css.formRow}>
          <div className={css.formGroup}>
            <label htmlFor="contact-name" className={css.label}>
              <FormattedMessage id="ContactUsForm.nameLabel" defaultMessage="Your Name" />
              <span className={css.required}>*</span>
            </label>
            <div className={css.inputWrapper}>
              <span className={css.inputIcon}>
                <IconCard brand="user" />
              </span>
              <input
                type="text"
                id="contact-name"
                name="FNAME"
                value={formData.FNAME}
                onChange={handleChange}
                onBlur={() => handleBlur('FNAME')}
                className={`${css.input} ${errors.FNAME ? css.inputError : ''}`}
                placeholder={intl.formatMessage({ 
                  id: 'ContactUsForm.namePlaceholder',
                  defaultMessage: 'Enter your full name'
                })}
                aria-invalid={!!errors.FNAME}
                aria-required="true"
              />
            </div>
            {errors.FNAME && <div className={css.error}>{errors.FNAME}</div>}
          </div>
        </div>

        <div className={css.formRow}>
          <div className={css.formGroup}>
            <label htmlFor="contact-email" className={css.label}>
              <FormattedMessage id="ContactUsForm.emailLabel" defaultMessage="Email Address" />
              <span className={css.required}>*</span>
            </label>
            <div className={css.inputWrapper}>
              <span className={css.inputIcon}>
                <IconCard brand="mail" />
              </span>
              <input
                type="email"
                id="contact-email"
                name="EMAIL"
                value={formData.EMAIL}
                onChange={handleChange}
                onBlur={() => handleBlur('EMAIL')}
                className={`${css.input} ${errors.EMAIL ? css.inputError : ''}`}
                placeholder={intl.formatMessage({ 
                  id: 'ContactUsForm.emailPlaceholder',
                  defaultMessage: 'your.email@example.com'
                })}
                aria-invalid={!!errors.EMAIL}
                aria-required="true"
              />
            </div>
            {errors.EMAIL && <div className={css.error}>{errors.EMAIL}</div>}
          </div>
        </div>

        <div className={css.formRow}>
          <div className={css.formGroup}>
            <label htmlFor="contact-phone" className={css.label}>
              <FormattedMessage id="ContactUsForm.phoneLabel" defaultMessage="Phone Number" />
              <span className={css.optional}> (optional)</span>
            </label>
            <div className={css.inputWrapper}>
              <span className={css.inputIcon}>
                <IconCard brand="phone" />
              </span>
              <input
                type="tel"
                id="contact-phone"
                name="PHONE"
                value={formData.PHONE}
                onChange={handleChange}
                className={css.input}
                placeholder={intl.formatMessage({ 
                  id: 'ContactUsForm.phonePlaceholder',
                  defaultMessage: '+1 (555) 123-4567'
                })}
              />
            </div>
          </div>
        </div>

        <div className={css.formRow}>
          <div className={css.formGroup}>
            <label htmlFor="contact-message" className={css.label}>
              <FormattedMessage id="ContactUsForm.messageLabel" defaultMessage="Your Message" />
              <span className={css.required}>*</span>
            </label>
            <textarea
              id="contact-message"
              name="MESSAGE"
              value={formData.MESSAGE}
              onChange={handleChange}
              onBlur={() => handleBlur('MESSAGE')}
              className={`${css.textarea} ${errors.MESSAGE ? css.inputError : ''}`}
              placeholder={intl.formatMessage({ 
                id: 'ContactUsForm.messagePlaceholder',
                defaultMessage: 'Tell us how we can help you...'
              })}
              rows="6"
              maxLength={MAX_MESSAGE_LENGTH}
              aria-invalid={!!errors.MESSAGE}
              aria-required="true"
            />
            <div className={css.characterCount}>
              {messageCharCount}/{MAX_MESSAGE_LENGTH} characters
            </div>
            {errors.MESSAGE && <div className={css.error}>{errors.MESSAGE}</div>}
          </div>
        </div>

        {errors.submit && <div className={css.submitError}>{errors.submit}</div>}
        {successMessage && <div className={css.success}>{successMessage}</div>}

        <div className={css.buttonWrapper}>
          <button
            type="submit"
            className={css.submitButton}
            disabled={hasErrors || isFormEmpty || loading}
          >
            {loading ? (
              <FormattedMessage id="ContactUsForm.sending" defaultMessage="SENDING..." />
            ) : (
              <FormattedMessage id="ContactUsForm.submit" defaultMessage="SEND MESSAGE" />
            )}
            {!loading && <IconCard brand="shareArrow" />}
          </button>
        </div>

        {/* MailChimp honeypot field */}
        <div style={{ position: 'absolute', left: '-5000px' }} aria-hidden="true">
          <input
            type="text"
            name="b_0530e140d1a3522fe688bd3e1_e01c29c204"
            tabIndex="-1"
            defaultValue=""
          />
        </div>
      </form>
    </div>
  );
};

export default ContactUsForm;
